// src/JourneyFullJournal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import JourneyService from "./services/JourneyService";
import { getBlueprintForProblem } from "./JourneyBlueprints";
import { dirForLang, normalizeLang } from "./i18n";
import { getSystemStrings } from "./strings/systemStrings";
import { useAuth } from "./auth/AuthContext";
import { resolveUserFacingText } from "./utils/userFacingText";
import "./JourneyFullJournal.css";

function useQuery() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const lang = normalizeLang(params.get("lang") || "he");
  const problemKey = params.get("problemKey") || "rel_breakup_fresh_pain";
  const runId = String(params.get("runId") || "").trim();
  return { lang, problemKey, runId, params, location };
}

function sortEntries(entries) {
  return [...entries].sort((a, b) => {
    const ai = Number(String(a.questionId || "").split("::")[1] || 0);
    const bi = Number(String(b.questionId || "").split("::")[1] || 0);
    if (ai !== bi) return ai - bi;
    return String(a.questionId || "").localeCompare(String(b.questionId || ""));
  });
}

export default function JourneyFullJournal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lang, problemKey, runId, location } = useQuery();
  const [meta, setMeta] = useState(null);
  const [entries, setEntries] = useState([]);
  const [query, setQuery] = useState("");
  const [dayFilter, setDayFilter] = useState("all");
  const blueprint = getBlueprintForProblem(problemKey, lang);

  useEffect(() => {
    if (runId) {
      JourneyService.setActiveRun(lang, problemKey, runId);
    }
    const { meta: m, entries: e } = JourneyService.getFullJournal(lang, problemKey, { runId });
    setMeta(m);
    setEntries(e || []);

    JourneyService.fetchJournalRecent(lang, problemKey, 200)
      .then((remoteEntries) => {
        if (!remoteEntries.length) return;
        JourneyService.mergeJournalEntriesIntoLocal(lang, problemKey, remoteEntries, { runId });
        const refreshed = JourneyService.getFullJournal(lang, problemKey, { runId });
        setMeta(refreshed.meta);
        setEntries(refreshed.entries || []);
      })
      .catch(() => {});
  }, [lang, problemKey, runId]);

  const t = getSystemStrings(lang, user?.gender).journal;
  const dir = dirForLang(lang);

  const dayOptions = useMemo(() => {
    const total = blueprint?.totalDays || meta?.totalDays || 0;
    if (total) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const uniqueDays = Array.from(new Set(entries.map((entry) => entry.dayNumber)))
      .filter((d) => Number.isFinite(Number(d)))
      .sort((a, b) => a - b);
    return uniqueDays;
  }, [entries, blueprint, meta]);

  const filteredEntries = useMemo(() => {
    const normalizedQuery = String(query || "").trim().toLowerCase();
    return entries.filter((entry) => {
      if (dayFilter !== "all" && Number(dayFilter) !== entry.dayNumber) return false;
      if (!normalizedQuery) return true;
      const haystack = `${entry.questionText || ""} ${entry.text || ""}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [entries, query, dayFilter]);

  const groupedByDay = useMemo(() => {
    const map = new Map();
    filteredEntries.forEach((entry) => {
      const day = entry.dayNumber;
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(entry);
    });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [filteredEntries]);

  const totalDays = blueprint?.totalDays || meta?.totalDays || dayOptions.length;

  const exportJson = () => {
    const payload = {
      meta: {
        lang,
        problemKey,
        problemTitle: meta?.problemTitle || "",
      },
      entries: filteredEntries,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `innervoice-journal-${problemKey}-${lang}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportTxt = () => {
    const lines = [];
    groupedByDay.forEach(([day, dayEntries]) => {
      lines.push(`${t.dayHeaderPrefix} ${day}`);
      sortEntries(dayEntries).forEach((entry, idx) => {
        lines.push(`${t.questionLabel} ${idx + 1}`);
        if (entry.questionText) lines.push(entry.questionText);
        lines.push(entry.text || "");
        lines.push("");
      });
      lines.push("");
    });
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `innervoice-journal-${problemKey}-${lang}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBack = () => {
    const sp = new URLSearchParams(location.search);
    sp.set("lang", lang);
    sp.set("problemKey", problemKey);
    navigate({ pathname: "/journey/overview", search: sp.toString() });
  };

  return (
    <div className="journal-full-page" dir={dir}>
      <header className="journal-header">
        <button type="button" className="journal-back-btn" onClick={handleBack}>
          {t.backToOverview}
        </button>
        <h1>{t.title}</h1>
        {meta && <p className="journal-meta">{meta.problemTitle}</p>}
      </header>

      <div className="journal-controls">
        <input
          className="journal-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.searchPlaceholder}
        />
        <select
          className="journal-filter"
          value={dayFilter}
          onChange={(e) => setDayFilter(e.target.value)}
        >
          <option value="all">{t.filterAll}</option>
          {dayOptions.map((day) => (
            <option key={`day-opt-${day}`} value={day}>
              {t.dayHeaderPrefix} {day}
            </option>
          ))}
        </select>
        <button type="button" className="journal-export" onClick={exportJson}>
          {t.exportJson}
        </button>
        <button type="button" className="journal-export" onClick={exportTxt}>
          {t.exportTxt}
        </button>
      </div>

      {(!entries || entries.length === 0) && <p className="journal-empty">{t.noEntries}</p>}

      {Array.from({ length: totalDays || 0 }, (_, i) => i + 1).map((day) => {
        const entryPair = groupedByDay.find(([d]) => d === day);
        const dayEntries = entryPair ? entryPair[1] : [];
        const daySubtitle = resolveUserFacingText(blueprint?.daySubtitles?.[day], {
          lang,
          gender: user?.gender,
          fallback: "",
        });
        return (
          <section key={`day-${day}`} className="journal-card">
            <h2>
              {t.dayHeaderPrefix} {day}
            </h2>
            {daySubtitle && <p className="journal-day-subtitle">{daySubtitle}</p>}
            {dayEntries.length === 0 && <div className="journal-entry-empty">{t.emptyDay}</div>}
            {sortEntries(dayEntries).map((entry, idx) => (
              <div key={`${entry.questionId || idx}`} className="journal-entry">
                <div className="journal-entry-label">
                  {t.questionLabel} {idx + 1}
                </div>
                {entry.questionText && (
                  <div className="journal-entry-question">{entry.questionText}</div>
                )}
                <div className="journal-entry-text">{entry.text}</div>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}
